/*
  <product-comparison>

  Purpose:
    Used by sections/product-comparison.liquid. Switches which comparison
    rows are visible (Saucen vs. Snacks) when a pill is clicked - no page
    reload, no fetch. Adapted from <tabbed-products>
    (component-tabbed-products.js): same accessible tab pattern
    (role=tablist/tab, aria-selected, roving tabindex, arrow/Home/End keys,
    Theme-Editor block-select support). The difference: instead of hiding/
    showing one <div role="tabpanel"> per tab, this toggles the `hidden`
    attribute on individual table rows matched by their data-category
    attribute, since one panel here is a set of non-contiguous rows rather
    than a single element.

  Behavior:
    - Click / Enter / Space on a pill: show the rows whose data-category
      matches, mark the pill active (aria-selected).
    - Left / Right arrow, Home, End move between the two pills (roving
      tabindex).
    - Theme Editor: selecting a comparison_row block switches to that row's
      category first (if needed) so the row being edited is visible.

  Dependencies:
    - assets/section-product-comparison.css
*/
if (!customElements.get('product-comparison')) {
  customElements.define('product-comparison', class ProductComparison extends HTMLElement {
    connectedCallback() {
      this.tablist = this.querySelector('[role="tablist"]');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.table = this.querySelector('.pc__table');
      if (!this.tablist || !this.tabs.length || !this.table) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(tab));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, tab));
      });

      // Theme Editor: selecting a comparison_row block shows its category
      this.addEventListener('shopify:block:select', (event) => {
        const row = event.target.closest && event.target.closest('.pc__row');
        if (!row) return;
        const category = row.dataset.category;
        const tab = this.tabs.find((t) => t.dataset.category === category);
        if (tab) this.activate(tab);
      });
    }

    rowsFor(category) {
      return Array.from(this.table.querySelectorAll('.pc__row[data-category]')).filter(
        (row) => row.dataset.category === category
      );
    }

    activate(tab, { focus = false } = {}) {
      if (tab.getAttribute('aria-selected') === 'true') return;

      this.tabs.forEach((other) => {
        const selected = other === tab;
        other.setAttribute('aria-selected', selected ? 'true' : 'false');
        other.setAttribute('tabindex', selected ? '0' : '-1');
      });

      const category = tab.dataset.category;
      this.table.querySelectorAll('.pc__row[data-category]').forEach((row) => {
        row.hidden = row.dataset.category !== category;
      });

      if (focus) tab.focus();
    }

    onKeydown(event, tab) {
      const index = this.tabs.indexOf(tab);
      let next = null;
      if (event.key === 'ArrowRight') next = this.tabs[(index + 1) % this.tabs.length];
      else if (event.key === 'ArrowLeft') next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
      else if (event.key === 'Home') next = this.tabs[0];
      else if (event.key === 'End') next = this.tabs[this.tabs.length - 1];
      if (!next) return;
      event.preventDefault();
      this.activate(next, { focus: true });
    }
  });
}
